import React, { useState, useEffect } from 'react';
import Navigation from './../components/Navigation';
import Footer from './../components/Footer';
import './Contact.css';

const Contact = (props) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    message: ''
  });
  
  const [emailjsLoaded, setEmailjsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Contact Us | Global Expedyte';
  }, []);

  // Load EmailJS
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
    script.async = true;
    script.onload = () => {
      window.emailjs.init('LNPMM5ZTcd6-UP0kx');
      setEmailjsLoaded(true);
    };
    script.onerror = () => {
      setStatus({
        type: 'error',
        message: 'Failed to load email service. Please try again or contact info@globalexpedyte.co.za'
      });
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (status.message) {
      setStatus({ type: '', message: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailjsLoaded) {
      setStatus({
        type: 'error',
        message: 'Email service is still loading. Please wait and try again.'
      });
      return;
    }

    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        company: formData.company || 'Not specified',
        inquiry_type: formData.inquiryType,
        message: formData.message,
        to_email: 'info@globalexpedyte.co.za'
      };

      await window.emailjs.send(
        'service_xk0o2i8',
        'template_contact',
        templateParams
      );
      
      setStatus({
        type: 'success',
        message: 'Message sent successfully! We will get back to you within 24 hours.'
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        inquiryType: '',
        message: ''
      });

    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Failed to send message. Please email us directly at info@globalexpedyte.co.za'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <Navigation {...props} />
      
      <div className="contact-content">
        <div className="contact-container">
          
          <div className="page-title">
            <h1>Contact Us</h1>
            <p>Have questions? Get in touch and we'll respond within 24 hours.</p>
          </div>

          <div className="contact-layout">
            
            <div className="contact-info">
              <div className="info-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                <div>
                  <h3>Email</h3>
                  <a href="mailto:info@globalexpedyte.co.za">info@globalexpedyte.co.za</a>
                </div>
              </div>

              <div className="info-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                </svg>
                <div>
                  <h3>Phone</h3>
                  <p>+27 (0) XX XXX XXXX</p>
                  <p className="info-note">Mon - Fri, 8AM - 5PM</p>
                </div>
              </div>

              <div className="info-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <div>
                  <h3>Location</h3>
                  <p>South Africa</p>
                  <p className="info-note">Nationwide service</p>
                </div>
              </div>
            </div>

            <div className="contact-form-wrapper">
              {status.message && (
                <div className={`status-message ${status.type}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {status.type === 'success' ? (
                      <polyline points="20 6 9 17 4 12"/>
                    ) : (
                      <>
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </>
                    )}
                  </svg>
                  <span>{status.message}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+27 XX XXX XXXX"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">Company</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company Name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="inquiryType">Inquiry Type *</label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="post-rfq">Post an RFQ / Hire Freelancers</option>
                    <option value="join-freelancer">Join as Freelancer</option>
                    <option value="partnership">Partnership Inquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="general">General Question</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="How can we help you?"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loading || !emailjsLoaded}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </div>

          </div>
        </div>
      </div>

      <Footer {...props} />
    </div>
  );
};

export default Contact;